import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, XCircle, ExternalLink, User, Loader2, BadgeCheck, BadgeAlert, ArrowUpRight } from 'lucide-react';
import { adminUsersAPI } from '../api';

const SignupProfile = ({ type }) => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [userResponse, submissionsResponse] = await Promise.all([
          adminUsersAPI.getById(id),
          adminUsersAPI.signupSubmissions({
            type,
            user_id: id,
            limit: 1
          })
        ]);

        setUser(userResponse?.data?.user || null);

        const rows = submissionsResponse?.data?.submissions || [];
        setSubmission(rows[0] || null);
      } catch (requestError) {
        console.error('Failed to fetch profile details:', requestError);
        setError(requestError.message || 'Failed to load profile details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, type]);

  const payload = submission?.payload || {};
  const categories = Array.isArray(payload.overlap_categories) ? payload.overlap_categories : [];
  const backlink = type === 'advertiser' ? '/advertisers' : '/affiliates';
  const title = type === 'advertiser' ? 'Advertiser Details' : 'Affiliate Details';
  const notesLabel = type === 'advertiser' ? 'Traffic Notes' : 'Offer Notes';
  const website = payload.website || payload.advertiser_lead?.AdvertiserCenterURL || user?.website || '-';
  const isEnrolled = Boolean(submission?.enrolled_lead_id);
  const deepLink = submission?.enrolled_lead_id
    ? `/discovery?pipeline=${encodeURIComponent(type)}&tab=qualified&origin=signup&leadId=${encodeURIComponent(submission.enrolled_lead_id)}`
    : `/discovery?pipeline=${encodeURIComponent(type)}&tab=qualified&origin=signup`;

  const handleEnroll = async () => {
    try {
      setEnrolling(true);
      setEnrollError('');
      const response = await adminUsersAPI.enrollSignupSubmission(submission.id);
      const enrolled = response?.data?.submission || response?.submission || response?.data || response || {};
      setSubmission((prev) => ({ ...prev, ...enrolled }));
    } catch (requestError) {
      setEnrollError(requestError.message || 'Failed to enroll this signup submission');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">{title}</h2>
          <p className="text-slate-400 mt-2">Full signup payload and account profile</p>
        </div>
        <Link
          to={backlink}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors inline-flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Link>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          {loading ? (
            <div className="h-72 flex items-center justify-center text-slate-400">Loading details...</div>
          ) : error ? (
            <div className="h-72 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          ) : !user ? (
            <div className="h-72 flex items-center justify-center text-slate-400">No matching user found.</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div>
                    <p className="text-slate-100 font-semibold text-lg inline-flex items-center">
                      <User className="w-5 h-5 mr-2 text-red-300" />
                      {user.name || '-'}
                    </p>
                    <p className="text-slate-300 text-sm">{user.email || '-'} {user.company ? `· ${user.company}` : ''}</p>
                    <p className="text-slate-500 text-xs mt-1">Created {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${isEnrolled ? 'text-emerald-300 bg-emerald-900/30 border border-emerald-700/40' : 'text-slate-300 bg-slate-800/40 border border-slate-700'}`}>
                      {isEnrolled ? <BadgeCheck className="w-3.5 h-3.5 mr-1" /> : <BadgeAlert className="w-3.5 h-3.5 mr-1" />}
                      {isEnrolled ? `Enrolled · ${submission.enrolled_lead_status || 'active'}` : 'Not enrolled'}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize text-blue-300 bg-blue-900/30 border border-blue-700/40">
                      {user.type}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                  <p className="text-slate-400 text-xs mb-1">First Name</p>
                  <p className="text-slate-200">{payload.first_name || '-'}</p>
                </div>
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                  <p className="text-slate-400 text-xs mb-1">Last Name</p>
                  <p className="text-slate-200">{payload.last_name || '-'}</p>
                </div>
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                  <p className="text-slate-400 text-xs mb-1">Website</p>
                  <p className="text-slate-200 break-words">{website}</p>
                </div>
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                  <p className="text-slate-400 text-xs mb-1">Approval Status</p>
                  <p className="text-slate-200 capitalize">{user.approval_status || '-'}</p>
                </div>
              </div>

              <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                <p className="text-slate-400 text-xs mb-1">Selected Categories</p>
                <p className="text-slate-200 text-sm">{categories.length > 0 ? categories.join(', ') : '-'}</p>
              </div>

              {payload.overlap_notes && (
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3">
                  <p className="text-slate-400 text-xs mb-1">{notesLabel}</p>
                  <p className="text-slate-200 text-sm">{payload.overlap_notes}</p>
                </div>
              )}

              {submission && (
                <div className="rounded-lg bg-slate-900/50 border border-slate-700/60 p-3 text-xs text-slate-300">
                  <p>External status: {submission.external_status || 'not_sent'}</p>
                  {submission.external_response_code && <p>Response code: {submission.external_response_code}</p>}
                  {submission.external_error && <p className="text-red-300 mt-1">Error: {submission.external_error}</p>}
                  {submission.external_post_url && (
                    <p className="mt-1 inline-flex items-center text-slate-400">
                      Endpoint captured
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                {submission?.id > 0 && (
                  <>
                    {isEnrolled ? (
                      <button
                        onClick={handleEnroll}
                        disabled={enrolling}
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:bg-slate-700 disabled:text-slate-400 text-slate-100 text-sm transition-colors"
                      >
                        {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Re-enroll in Lead Intelligence
                      </button>
                    ) : (
                      <button
                        onClick={handleEnroll}
                        disabled={enrolling}
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 disabled:bg-slate-700 disabled:text-slate-400 text-white text-sm transition-colors"
                      >
                        {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Enroll in Lead Intelligence
                      </button>
                    )}
                  </>
                )}

                {isEnrolled && (
                  <Link to={deepLink} className="inline-flex items-center text-sm text-red-300 hover:text-red-200 transition-colors">
                    Open in Lead Intelligence
                    <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Link>
                )}
              </div>
              {enrollError && <p className="text-sm text-red-300">{enrollError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupProfile;
